/**
 * Pure, client-safe formatting for the area pages: row stats, numbers, months and day names. No
 * server imports, so client components can use it.
 */
import type { AreaRowStats } from "@/lib/areas/row-model";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export function monthName(month: number): string {
  return MONTH_NAMES[month - 1] ?? "";
}

/**
 * A rounded number for display, with a true minus sign (U+2212) for negatives, as the mockups
 * print temperatures ("low −5.5°C"); never "-0".
 */
export function formatNumber(value: number, places = 0): string {
  const factor = 10 ** places;
  const rounded = Math.round(value * factor) / factor;
  if (rounded === 0) return "0";
  return rounded < 0 ? `\u2212${String(-rounded)}` : String(rounded);
}

const round = formatNumber;

/** Short stat fragments for a row, by stat family. */
export function rowStatParts(stats: AreaRowStats): readonly string[] {
  const parts: string[] = [];
  switch (stats.kind) {
    case "surf":
      if (stats.swellM !== null) parts.push(`${round(stats.swellM, 1)} m swell${stats.periodS !== null ? ` at ${round(stats.periodS)} s` : ""}`);
      if (stats.windKmh !== null) parts.push(`wind ${round(stats.windKmh)} km/h${stats.windRelation ? ` ${stats.windRelation}` : ""}`);
      else if (stats.windRelation) parts.push(`${stats.windRelation} wind`);
      if (stats.waterC !== null) parts.push(`water ${round(stats.waterC)}°C`);
      break;
    case "snow":
      if (stats.newSnow48hCm !== null && stats.newSnow48hCm >= 1) parts.push(`${round(stats.newSnow48hCm)} cm new in 48 hours`);
      if (stats.depthCm !== null && stats.depthCm > 0) parts.push(`base ${round(stats.depthCm)} cm`);
      if (stats.extent !== null) parts.push(`${round(stats.extent * 100)}% open`);
      if (stats.tempMinC !== null) parts.push(`low ${round(stats.tempMinC)}°C`);
      break;
    case "wind":
      if (stats.windKmh !== null) parts.push(`wind ${round(stats.windKmh)} km/h`);
      if (stats.waterC !== null) parts.push(`water ${round(stats.waterC)}°C`);
      break;
    case "water":
      if (stats.waterC !== null) parts.push(`water ${round(stats.waterC)}°C`);
      if (stats.swellM !== null) parts.push(`${round(stats.swellM, 1)} m swell`);
      break;
    case "trail":
      if (stats.tempMinC !== null) parts.push(`low ${round(stats.tempMinC)}°C`);
      if (stats.windKmh !== null) parts.push(`wind ${round(stats.windKmh)} km/h`);
      break;
    case "sailing":
      if (stats.windKmh !== null) parts.push(`wind ${round(stats.windKmh)} km/h`);
      if (stats.venueType) parts.push(stats.venueType);
      break;
  }
  return parts;
}

/** "Sat 4 Oct" for an ISO date, read as a calendar date (no timezone shift). */
export function dayLabel(dateISO: string): string {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })
    .format(new Date(`${dateISO}T12:00:00Z`))
    .replace(/,/gu, "");
}

/** "Sat" for an ISO date. */
export function weekdayShort(dateISO: string): string {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(new Date(`${dateISO}T12:00:00Z`));
}

/** Local calendar date and fractional hour of an instant in a timezone. */
export function localClock(nowMs: number, timezone: string): Readonly<{ dateISO: string; hour: number }> {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: intlTimeZoneAt(timezone || "UTC", nowMs),
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(nowMs));
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return { dateISO: `${get("year")}-${get("month")}-${get("day")}`, hour: Number(get("hour")) + Number(get("minute")) / 60 };
}
